// frontend/src/hooks/useEmails.ts - NEW EMAIL-SPECIFIC HOOKS
// Entity-specific hooks for emails separated from core useApi.ts
// Insert this as a new file: useEmails.ts

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  
  // Email types
  Email,
  EmailQueryParams,
  EmailPreservationRequest,
  EmailBatchOperationRequest,
  EmailRetentionPolicyRequest,
  EmailCleanupRequest,
  
  // Correspondence types
  Correspondence,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== EMAIL QUERY HOOKS =====

/**
 * Fetch paginated list of emails with comprehensive filtering
 */
export const useEmails = (params?: EmailQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Email>> => {
      
      const response = await apiClient.emails.list(params);

      return normalizePaginatedResponse<Email>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'emails_list',
      ...options
    }
  );
};

/**
 * Fetch single email by ID
 */
export const useEmail = (id?: string, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<Email> => {
      validateObjectId(id, 'Email ID');


      const response = await apiClient.emails.get(id!);

      // Backend returns { email: {...}, metadata: {...} } after handleResponse unwraps
      const normalized = normalizeSingleResponse<any>(response);
      return (normalized?.email || normalized) as Email;
    },
    {
      immediate: !!id,
      dependencies: [id],
      rateLimitKey: `email_${id}`,
      ...options
    }
  );
};

/**
 * Search emails with advanced filtering
 */
export const useSearchEmails = (query?: string, filters?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Email>> => {
      if (!query?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      // Use list endpoint with search parameter
      const searchParams: EmailQueryParams = {
        search: query,
        ...filters
      };

      const response = await apiClient.emails.list(searchParams);
      return normalizePaginatedResponse<Email>(response);
    },
    {
      immediate: !!query?.trim(),
      dependencies: [query, JSON.stringify(filters || {})],
      debounce: 300,
      retryCount: 2,
      rateLimitKey: 'search_emails',
      ...options
    }
  );
};

/**
 * Get email analytics and statistics
 */
export const useEmailAnalytics = (params?: any, options?: UseApiOptions) => {
  return useApi(
    async () => {
      
      const response = await apiClient.emails.getAnalytics(params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for analytics
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 1,
      rateLimitKey: 'email_analytics',
      ...options
    }
  );
};

/**
 * Get email preservation statistics
 */
export const useEmailPreservationStats = (options?: UseApiOptions) => {
  return useApi(
    async () => {
      
      const response = await apiClient.emails.getPreservationStats();
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for stats
      rateLimitKey: 'email_preservation_stats',
      ...options
    }
  );
};

/**
 * Get email synchronization status
 */
export const useEmailSyncStatus = (accountId?: string, options?: UseApiOptions) => {
  return useApi(
    async () => {
      if (accountId) {
        validateObjectId(accountId, 'Account ID');
      }
      
      
      const response = await apiClient.emails.getSyncStatus(accountId);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: false, // Manual trigger for sync status
      dependencies: [accountId],
      rateLimitKey: `email_sync_status_${accountId || 'all'}`,
      ...options
    }
  );
};

// ===== EMAIL MUTATION HOOKS =====

/**
 * Delete email with confirmation
 */
export const useDeleteEmail = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (emailId: string): Promise<void> => {
      validateObjectId(emailId, 'Email ID');
      
      
      const response = await apiClient.emails.delete(emailId);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

/**
 * Mark email as read
 */
export const useMarkEmailAsRead = (options?: UseMutationOptions<Email, string>) => {
  return useMutation(
    async (emailId: string): Promise<Email> => {
      validateObjectId(emailId, 'Email ID');
      
      
      const response = await apiClient.emails.markRead(emailId);
      
      return normalizeSingleResponse<Email>(response);
    },
    options
  );
};

/**
 * Preserve single email
 */
export const usePreserveEmail = (options?: UseMutationOptions<any, EmailPreservationRequest>) => {
  return useMutation(
    async (data: EmailPreservationRequest): Promise<any> => {
      // Validation
      if (!data.emailIds || data.emailIds.length === 0) {
        throw new Error('At least one email ID is required');
      }
      
      validateObjectIds(data.emailIds, 'Email IDs');
      
      if (!data.reason?.trim()) {
        throw new Error('Preservation reason is required');
      }
      
      
      const response = await apiClient.emails.preserve(data);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Emergency preserve emails
 */
export const useEmergencyPreserveEmail = (options?: UseMutationOptions<any, { emailIds: string[]; reason: string }>) => {
  return useMutation(
    async ({ emailIds, reason }: { emailIds: string[]; reason: string }): Promise<any> => {
      // Validation
      if (!emailIds || emailIds.length === 0) {
        throw new Error('At least one email ID is required');
      }
      
      validateObjectIds(emailIds, 'Email IDs');
      
      if (!reason?.trim()) {
        throw new Error('Emergency preservation reason is required');
      }
      
      
      const response = await apiClient.emails.emergencyPreserve(emailIds, reason);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Convert email to correspondence
 */
export const useConvertEmailToCorrespondence = (options?: UseMutationOptions<Correspondence, { emailId: string; projectId?: string; customFields?: Array<{ fieldId: string; name: string; type: string; value: any }>; sourceDocument?: { type: 'attachment'; attachmentId: string }; selectedAttachmentIds?: string[] }>) => {
  return useMutation(
    async ({ emailId, projectId, customFields, sourceDocument, selectedAttachmentIds }: { emailId: string; projectId?: string; customFields?: Array<{ fieldId: string; name: string; type: string; value: any }>; sourceDocument?: { type: 'attachment'; attachmentId: string }; selectedAttachmentIds?: string[] }): Promise<Correspondence> => {
      validateObjectId(emailId, 'Email ID');

      if (projectId) {
        validateObjectId(projectId, 'Project ID');
      }

      const response = await apiClient.emails.convertToCorrespondence(emailId, projectId, customFields, sourceDocument, selectedAttachmentIds);

      return normalizeSingleResponse<Correspondence>(response);
    },
    options
  );
};

/**
 * Batch email operations
 */
export const useEmailBatchOperations = (options?: UseMutationOptions<any, EmailBatchOperationRequest>) => {
  return useMutation(
    async (data: EmailBatchOperationRequest): Promise<any> => {
      // Validation
      if (!data.operation) {
        throw new Error('Batch operation type is required');
      }
      
      if (!data.emailIds || data.emailIds.length === 0) {
        throw new Error('At least one email ID is required');
      }
      
      if (!['preserve', 'delete', 'update_retention', 'emergency_preserve'].includes(data.operation)) {
        throw new Error('Invalid operation. Must be preserve, delete, update_retention, or emergency_preserve');
      }
      
      validateObjectIds(data.emailIds, 'Email IDs');
      
      // Additional validation for specific operations
      if (data.operation === 'preserve' && !data.reason?.trim()) {
        throw new Error('Preservation reason is required for preserve operation');
      }
      
      
      // Route to appropriate batch endpoint based on operation
      let response;
      
      switch (data.operation) {
        case 'preserve':
          response = await apiClient.emails.batchPreserve(data);
          break;

        case 'emergency_preserve':
          response = await apiClient.emails.emergencyPreserve(data.emailIds, data.reason || 'Emergency preservation');
          break;

        case 'delete':
          response = await apiClient.emails.batchDelete(data.emailIds);
          break;
          
        case 'update_retention':
          // Handle retention updates
          if (apiClient.emails && 'batchUpdateRetention' in apiClient.emails) {
            response = await (apiClient.emails as any).batchUpdateRetention(data);
          } else {
            throw new Error('Batch retention update not available');
          }
          break;
          
        default:
          throw new Error(`Batch operation '${data.operation}' not implemented`);
      }
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Batch convert emails to correspondences
 */
export const useBatchConvertEmails = (options?: UseMutationOptions<any, { emailIds: string[]; projectId?: string }>) => {
  return useMutation(
    async ({ emailIds, projectId }: { emailIds: string[]; projectId?: string }): Promise<any> => {
      if (!emailIds || emailIds.length === 0) {
        throw new Error('At least one email ID is required');
      }
      
      validateObjectIds(emailIds, 'Email IDs');
      
      if (projectId) {
        validateObjectId(projectId, 'Project ID');
      }
      
      
      const response = await apiClient.emails.batchConvert(emailIds, projectId);
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Batch mark emails as read
 */
export const useBatchMarkEmailsAsRead = (options?: UseMutationOptions<any, string[]>) => {
  return useMutation(
    async (emailIds: string[]): Promise<any> => {
      if (!emailIds || emailIds.length === 0) {
        throw new Error('At least one email ID is required');
      }
      
      validateObjectIds(emailIds, 'Email IDs');
      
      
      const response = await apiClient.emails.batchMarkRead(emailIds);
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Synchronize email account
 */
export const useSyncEmailAccount = (options?: UseMutationOptions<any, string>) => {
  return useMutation(
    async (accountId: string): Promise<any> => {
      validateObjectId(accountId, 'Account ID');
      
      
      const response = await apiClient.emails.syncAccount(accountId);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Synchronize all email accounts
 */
export const useSyncAllEmails = (options?: UseMutationOptions<any, void>) => {
  return useMutation(
    async (): Promise<any> => {
      
      const response = await apiClient.emails.syncAll();
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Update email retention policy (organization-level)
 */
export const useUpdateEmailRetention = (options?: UseMutationOptions<any, { organizationId: string; data: EmailRetentionPolicyRequest }>) => {
  return useMutation(
    async ({ organizationId, data }: { organizationId: string; data: EmailRetentionPolicyRequest }): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      
      // Validation
      if (!data.emailRetentionDays || data.emailRetentionDays < 1) {
        throw new Error('Email retention days must be at least 1');
      }
      
      if (!data.reason?.trim()) {
        throw new Error('Reason for retention policy update is required');
      }
      
      
      const response = await apiClient.organizations.updateRetentionPolicy(organizationId, data);
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Cleanup emails based on retention policies
 */
export const useCleanupEmails = (options?: UseMutationOptions<any, { organizationId: string; data: EmailCleanupRequest }>) => {
  return useMutation(
    async ({ organizationId, data }: { organizationId: string; data: EmailCleanupRequest }): Promise<any> => {
      validateObjectId(organizationId, 'Organization ID');
      
      
      const response = await apiClient.organizations.cleanupEmails(organizationId, data);
      return normalizeSingleResponse(response);
    },
    options
  );
};

// ===== EXPORTS =====

// Export all email-related hooks
export {
  // Query hooks
  // useEmails, useEmail - already exported above
  // useSearchEmails - already exported above
  // useEmailAnalytics, useEmailPreservationStats, useEmailSyncStatus - already exported above
  
  // Mutation hooks  
  // useDeleteEmail, useMarkEmailAsRead - already exported above
  // usePreserveEmail, useEmergencyPreserveEmail - already exported above
  // useConvertEmailToCorrespondence - already exported above
  // useEmailBatchOperations - already exported above
  // useBatchConvertEmails, useBatchMarkEmailsAsRead - already exported above
  // useSyncEmailAccount, useSyncAllEmails - already exported above
  // useUpdateEmailRetention, useCleanupEmails - already exported above
};

// Re-export validation utilities for convenience
export { validateObjectId, validateObjectIds } from './useApi';

// Default export for convenience
const emailHooks = {
  // Query hooks
  useEmails,
  useEmail,
  useSearchEmails,
  useEmailAnalytics,
  useEmailPreservationStats,
  useEmailSyncStatus,
  
  // Mutation hooks
  useDeleteEmail,
  useMarkEmailAsRead,
  usePreserveEmail,
  useEmergencyPreserveEmail,
  useConvertEmailToCorrespondence,
  useEmailBatchOperations,
  useBatchConvertEmails,
  useBatchMarkEmailsAsRead,
  useSyncEmailAccount,
  useSyncAllEmails,
  useUpdateEmailRetention,
  useCleanupEmails
};

export default emailHooks;